import { colors, spacing } from '@akin-home/theme';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, PanResponder, View, ViewStyle } from 'react-native';

interface TeleopJoystickProps {
  onChange: (data: { x: number; y: number }) => void;
  size: number;
  throttle?: number;
  stickyRate?: number;
  style?: ViewStyle;
  disabled?: boolean;
}

export const TeleopJoystick = (props: TeleopJoystickProps) => {
  const pan = useRef(new Animated.ValueXY()).current;
  const latestVal = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isActive, setIsActive] = useState<boolean>(false);

  /**
   * Wrapper of props.onChange that handles throttling
   */
  const throttleTimer = useRef<any>(null);
  const handleOnChange = useCallback(
    (coords: { x: number; y: number }) => {
      // eslint-disable-next-line eqeqeq
      if (coords.x == undefined || coords.y == undefined) {
        return;
      }

      if (props.size > 0) {
        coords = {
          x: coords.x / props.size,
          y: -coords.y / props.size, // inverted due to conventions
        };
      } else {
        coords = {
          x: 0,
          y: 0,
        };
      }

      if (props.throttle) {
        // Check if the timeout since last call has passed. If it hasn't, then we don't do anything
        if (throttleTimer.current === null) {
          props.onChange(coords);

          throttleTimer.current = setTimeout(() => {
            throttleTimer.current = null;
          }, props.throttle);
        }
      } else {
        props.onChange(coords);
      }
    },
    [props]
  );

  /**
   * Handle sending data when throttle is held still at the sticky rate
   */
  useEffect(() => {
    if (props.stickyRate && isActive) {
      const id = setInterval(
        () => handleOnChange(latestVal.current),
        props.stickyRate
      );

      return () => clearInterval(id);
    }
  }, [handleOnChange, isActive, props.stickyRate]);

  /**
   * Handle adding listeners to the pan changes
   */
  useEffect(() => {
    const id = pan.addListener((coords) => {
      handleOnChange(coords);
      latestVal.current = coords;
    });

    return () => pan.removeListener(id);
  }, [handleOnChange, pan, props]);

  /**
   * Pan responder
   */
  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: () => true,

      onPanResponderStart: (e, gs) => {
        setIsActive(true);
      },

      onPanResponderMove: (e, gs) => {
        let dist = Math.sqrt(gs.dx * gs.dx + gs.dy * gs.dy);

        let actualDx = 0;
        let actualDy = 0;
        if (dist !== 0) {
          actualDx = gs.dx / dist;
          actualDy = gs.dy / dist;
        }

        dist = Math.min(dist, props.size);
        Animated.event([null, { dx: pan.x, dy: pan.y }], {
          useNativeDriver: false,
        })(e, {
          dx: actualDx * dist,
          dy: actualDy * dist,
        });
      },

      onPanResponderTerminate: (evt, gs) => {
        setIsActive(false);
        pan.setValue({ x: 0, y: 0 });
        props.onChange({ x: 0, y: 0 }); // Trigger it without throttling to make sure it goes through
      },
      onPanResponderRelease: () => {
        setIsActive(false);
        pan.setValue({ x: 0, y: 0 });
        props.onChange({ x: 0, y: 0 }); // Trigger it without throttling to make sure it goes through
      },
    })
  ).current;

  return (
    <View
      style={[
        { padding: props.size / 2 + spacing[1], borderRadius: 10 },
        props.style,
      ]}
    >
      <View
        style={[
          {
            height: 2 * props.size,
            width: 2 * props.size,
            borderRadius: props.size,
            backgroundColor: props.disabled ? 'lightgrey' : '#4b9ed6',
            justifyContent: 'center',
            alignItems: 'center',
          },
        ]}
      >
        <Animated.View
          style={{
            transform: [{ translateX: pan.x }, { translateY: pan.y }],
          }}
          {...panResponder.panHandlers}
          pointerEvents={props.disabled ? 'none' : 'auto'}
        >
          <View
            style={{
              height: props.size,
              width: props.size,
              borderRadius: props.size / 2,
              backgroundColor: props.disabled
                ? colors.grey.dim
                : colors.primary[900],
            }}
          />
        </Animated.View>
      </View>
    </View>
  );
};
